import { CanceledError } from 'axios';
import { nextTick, watch } from 'vue';

import { useBrokerStore } from '@/features/Broker/store';
import { useSimpleChoicesStore } from '@/features/ChoicesLists/Simple/store';
import { useClientStore } from '@/features/Clients';
import { useGlobalStore } from '@/features/Global/store';
import { useProfileStore } from '@/features/Profile';
import { useQuoteStore } from '@/features/Quotes/store';
import { QUOTE_TYPE } from '@/features/Quotes/types';
import type { WEBREPORT_TYPE } from '@/features/Sparten/types';
import { STEP } from '@/router/steps';
import type { ModuleGuards, WebreportModuleGuards } from '@/router/types';
import { VersicherungsangabenModuleGuards } from '@/router/types';
import refreshSession from '@/router/utils/refreshSession';
import { getRoute } from '@/router/utils/routeNames';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { useErrorHandler } from '@/services/errorHandler/useErrorHandler';
import router from '@/router';
import { isNoneEmptyString } from '@/utils/strings/strings';
import { useLogger } from '@/services/logger/useLogger';
import { LOGGER_MODULE } from '@/services/logger/types';
import { SessionError } from '@/services/errorHandler/SessionError';
import { getApiHttpClientInstance } from '@/services/api/useApi';
import { useIntercomChatBot } from '@demv_systems/feu-intercom';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { useContextParameters } from '@/router/utils/useContextParameters';
import { getLoggingHttpClientInstance } from '@/services/api/useLoggingApi';

const SessionToken = '_session';

export const guards: ModuleGuards = {
  /**
   * Factory function to create a vue-router navigation guard.
   * This guard sets which sparte is active in the global store
   * and triggers the authentication process in the broker store.
   * Also ensures that the first page we see is the 'Vorgaben' Page.
   *
   * @param spartenConfig A sparten config object
   * @returns vue-router NavigationGuard
   */
  beforeEnterModuleGuard: (spartenConfig) => (to, from, next) => {
    if (to.query[SessionToken] === undefined) {
      const params = new URLSearchParams(window.location.search);
      params.append(SessionToken, '1');

      // Calculate before navigation to preserve correct route info
      let isOffersWithoutId: boolean;
      let pathToPreserve: string | undefined = to.path;

      try {
        const offersRoute = getRoute(STEP.OFFERS);
        isOffersWithoutId =
          to.name === offersRoute &&
          (!to.params.berechnungsid || to.params.berechnungsid === '');
        pathToPreserve = isOffersWithoutId ? undefined : to.path;
      } catch (error) {
        pathToPreserve = to.path;
      }

      next({ name: 'login' });
      /**
       * fixes "NetworkError when attempting to fetch resource." errors.
       * isReady resolves only when the async deps are finished loading.
       * It might take a few milliseconds longer but doesn't create so
       * much noise.
       */
      router.isReady().then(() => {
        refreshSession(spartenConfig.props.sparte, params, pathToPreserve);
      });
      return;
    }

    const globaleStore = useGlobalStore();
    const brokerStore = useBrokerStore();
    // init client store and setup base client
    useClientStore();

    globaleStore.sparte = spartenConfig.props.sparte;
    brokerStore
      .loadBroker()
      .then(() => {
        useTagManager().init(
          brokerStore.id.toString(),
          brokerStore.isAdmin,
          import.meta.env.VITE_APP_GTM_ID,
        );
      })
      .then(() => globaleStore.initApp())
      .then(() =>
        nextTick(() => {
          if (brokerStore.broker.data?.intercomUserHash !== null) {
            useIntercomChatBot().install(
              import.meta.env.VITE_APP_INTERCOM_APP_ID ?? '',
              brokerStore.broker.data?.intercomUserHash ?? '',
              brokerStore.id.toString(),
              {
                email: brokerStore.broker.data?.email,
              },
            );
          }
        }),
      )
      .catch((error) => {
        if (error instanceof SessionError) {
          const urlParams = new URLSearchParams(window.location.search); // after the `next` call it would be empty

          next({ name: 'login' });
          router.isReady().then(() => {
            refreshSession(error.getSparte(), urlParams, to.path);
          });
          return;
        }

        if (error instanceof CanceledError) {
          next({
            name: 'Error',
          });
          return;
        }

        if (error instanceof VglError) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.GENERAL_SETUP, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      })
      .finally(() => {
        // Always make the input page the first thing we see
        const inputPage = getRoute(STEP.INPUT);

        // Allow direct access to offers page with berechnungsid parameter
        if (
          to.name === getRoute(STEP.OFFERS) &&
          to.params.berechnungsid &&
          to.params.berechnungsid !== ''
        ) {
          next();
          return;
        }

        if (from.path !== '/' || to.name === inputPage) {
          next();
          return;
        }

        next({
          name: inputPage,
          query: to.query,
          params: to.params,
        });
      });
  },

  afterEachGuard: (to) => {
    const {
      angebotId: quoteId,
      bestandsvertragId: inventoryContractId,
      clientId,
      profileId,
      bedarfId,
    } = to.query;

    const { webreportId } = to.params;

    const errorHandler = useErrorHandler();
    const contextParameters = useContextParameters();

    if (
      isNoneEmptyString(quoteId) &&
      !contextParameters.queryParamMap.has('angebotId')
    ) {
      contextParameters.addParam('angebotId', quoteId);
      useQuoteStore()
        .loadQuoteById(quoteId)
        .then(() => {
          useLogger(LOGGER_MODULE.QUOTE).info(
            `quote with id ${quoteId} successfully loaded`,
          );
        })
        .catch((e) => {
          useLogger(LOGGER_MODULE.QUOTE).error(
            `quote with id ${quoteId} failed to load`,
          );
          errorHandler.handle(
            e,
            null,
            `afterEachGuard -> load quote ${quoteId}`,
          );
        });
    } else if (
      isNoneEmptyString(inventoryContractId) &&
      !contextParameters.queryParamMap.has('bestandsvertragId')
    ) {
      contextParameters.addParam('bestandsvertragId', inventoryContractId);
      useQuoteStore()
        .loadQuoteById(inventoryContractId, QUOTE_TYPE.INVENTORY_CONTRACT)
        .then(() => {
          useLogger(LOGGER_MODULE.INVENTORY_CONTRACT).info(
            `inventory contract with id ${inventoryContractId} successfully loaded`,
          );
        })
        .catch((e) => {
          useLogger(LOGGER_MODULE.INVENTORY_CONTRACT).error(
            `inventory contract with id ${inventoryContractId} failed to load`,
          );
          errorHandler.handle(
            e,
            null,
            `afterEachGuard -> load inventory contract ${inventoryContractId}`,
          );
        });
    } else if (
      isNoneEmptyString(clientId) &&
      !contextParameters.queryParamMap.has('clientId')
    ) {
      contextParameters.addParam('clientId', clientId);
      useClientStore()
        .loadClientById(clientId, undefined, true)
        .then(() => {
          useLogger(LOGGER_MODULE.CLIENT).info(
            `client with id ${clientId} successfully loaded`,
          );
        })
        .catch((e) => {
          useLogger(LOGGER_MODULE.CLIENT).error(
            `client with id ${clientId} failed to load`,
          );
          errorHandler.handle(
            e,
            null,
            `afterEachGuard -> load client ${clientId}`,
          );
        });
    } else if (isNoneEmptyString(webreportId)) {
      useQuoteStore()
        .loadWebreportById(webreportId, to.meta.type as WEBREPORT_TYPE)
        .then(() => {
          useLogger(LOGGER_MODULE.WEBREPORTS).info(
            `webreport with id ${webreportId} (type: ${to.meta.type}) successfully loaded`,
          );
        })
        .catch((e) => {
          useLogger(LOGGER_MODULE.WEBREPORTS).error(
            `webreport with id ${webreportId} (type: ${to.meta.type}) failed to load`,
          );

          errorHandler.handle(
            new VglError(VGL_ERROR_CODE.QUOTE_WEBREPORT_NOT_FOUND, {
              cause: e,
              isNonClosableUserModal: true,
            }),
            null,
            `afterEachGuard -> load webreport ${webreportId}`,
          );
        });
    }

    if (
      isNoneEmptyString(profileId) &&
      !contextParameters.queryParamMap.has('profileId')
    ) {
      contextParameters.addParam('profileId', profileId);
      const profileStore = useProfileStore();

      const stopWatching = watch(
        () => [profileStore.isLoading, profileStore.data.length],
        ([loading, profileCount]) => {
          if (!(loading as boolean) && (profileCount as number) > 0) {
            const profile = profileStore.data.find(
              (p) => p.id === Number(profileId),
            );

            if (profile) {
              useLogger(LOGGER_MODULE.PROFILE).info(
                'initial profile selected',
                profile,
              );
              profileStore.setActive(profile);
            }

            stopWatching();
          }
        },
        {
          immediate: true,
        },
      );
    }

    if (
      isNoneEmptyString(bedarfId) &&
      isNoneEmptyString(clientId) &&
      !contextParameters.queryParamMap.has('bedarfId') &&
      !contextParameters.queryParamMap.has('clientId')
    ) {
      contextParameters.addParam('bedarfId', bedarfId);
      contextParameters.addParam('clientId', clientId);

      const brokerStore = useBrokerStore();
      brokerStore.bedarfInformation.bedarfId = bedarfId;
      brokerStore.bedarfInformation.clientId = clientId;

      getApiHttpClientInstance().setBedarfId(bedarfId);
    }
  },

  beforeEach: (to) => {
    let missingParam = false;
    Object.keys(useContextParameters().getParams()).forEach((param) => {
      if (to.query[param] === undefined) {
        missingParam = true;
      }
    });

    if (missingParam) {
      return {
        ...to,
        query: {
          ...to.query,
          ...useContextParameters().getParams(),
        },
      };
    }
  },
};

export const webreportGuards: WebreportModuleGuards = {
  /**
   * Factory function to create a vue-router navigation guard.
   * Only used for webreports!
   *
   * @param spartenConfig A sparten config object
   * @param type Webreport Type
   * @returns vue-router NavigationGuard
   */
  beforeEnterModuleGuard: (spartenConfig, type) => (to, _from, next) => {
    const globaleStore = useGlobalStore();
    globaleStore.sparte = spartenConfig.props.sparte;
    globaleStore.isWebreport = true;
    globaleStore.webreportType = type;

    const { token } = to.query;

    if (isNoneEmptyString(token)) {
      getApiHttpClientInstance().setToken(token);
      getLoggingHttpClientInstance().setToken(token);
    }

    useSimpleChoicesStore()
      .preloadSpartenChoices()
      .then(() => {
        next();
      });
  },
};

export const versicherungsangabenGuards: VersicherungsangabenModuleGuards = {
  beforeEnterModuleGuard: (spartenConfig) => (to, _from, next) => {
    const globaleStore = useGlobalStore();
    globaleStore.sparte = spartenConfig.props.sparte;
    globaleStore.isVersicherungsangabenContext = true;

    const { token } = to.query;

    if (isNoneEmptyString(token)) {
      getApiHttpClientInstance().setToken(token);
      getLoggingHttpClientInstance().setToken(token);
    }

    useSimpleChoicesStore()
      .preloadSpartenChoices()
      .then(() => {
        useTagManager().init(
          'guest',
          false,
          import.meta.env.VITE_APP_GTM_ID_VA,
        );

        next();
      });
  },
};
